import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from "sonner";
import { generateUniqueNickname } from '@/utils/nicknameGenerator';
import analytics from '@/utils/analytics';
import { User, OAuthProvider } from '@/types/user';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (userData: User, inviteCode?: string) => void;
  oauthLogin: (provider: OAuthProvider) => Promise<void>;
  logout: () => void;
  completeOnboarding: (preferences: { 
    nickname?: string, 
    notificationPreference?: 'email' | 'whatsapp' | 'both' | 'none',
    consentToDataTracking: boolean
  }) => void;
  setAdminStatus: (isAdmin: boolean) => void; // New function to set admin status
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load user data from localStorage
  useEffect(() => {
    const loadUserData = async () => {
      try {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check for token in localStorage (would be a JWT in real implementation)
        const token = localStorage.getItem('auth_token');
        
        if (token) {
          // Load stored user from localStorage for demo
          const storedUser = localStorage.getItem('currentUser');
          if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            // Convert date strings back to Date objects
            parsedUser.dateJoined = new Date(parsedUser.dateJoined);
            parsedUser.lastLogin = new Date();
            
            setUser(parsedUser);
            
            // Track login event
            analytics.trackEvent('user_login', { 
              userId: parsedUser.id,
              nickname: parsedUser.nickname,
              isAdmin: parsedUser.isAdmin
            });
            
            // Update last login
            localStorage.setItem('currentUser', JSON.stringify({
              ...parsedUser,
              lastLogin: new Date()
            }));
          }
        }
        
        setError(null);
      } catch (error) {
        console.error("Error loading user data:", error);
        setError("Failed to load user data. Please refresh the page.");
        toast.error("Failed to load user data. Please refresh the page.");
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, []);

  // OAuth login function
  const oauthLogin = async (provider: OAuthProvider): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Simulate API delay for OAuth verification
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Create a user from the OAuth provider data
      const userData: User = {
        id: `user_${provider.name}_${Math.random().toString(36).substring(2, 15)}`,
        name: provider.displayName || provider.email.split('@')[0],
        email: provider.email,
        nickname: generateUniqueNickname(),
        avatar: provider.photoURL || 'https://randomuser.me/api/portraits/women/44.jpg',
        isAdmin: provider.email.includes('admin'), // Simple mock for admin detection
        hasCompletedOnboarding: false,
        dateJoined: new Date(),
        lastLogin: new Date()
      };
      
      // Store the user in localStorage and set auth token
      localStorage.setItem('currentUser', JSON.stringify(userData));
      localStorage.setItem('auth_token', `mock-oauth-token-${provider.name}-${Math.random()}`);
      
      setUser(userData);
      
      setError(null);
      toast.success(`Signed in with ${provider.name.charAt(0).toUpperCase() + provider.name.slice(1)}`);
      
    } catch (error) {
      console.error(`Error during ${provider.name} OAuth login:`, error);
      setError(`Failed to sign in with ${provider.name}. Please try again.`);
      toast.error(`Failed to sign in with ${provider.name}. Please try again.`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Login function
  const login = (userData: User, inviteCode?: string) => {
    try {
      // Update the user object
      const updatedUser = {
        ...userData,
        lastLogin: new Date()
      };
      
      // Store the user in localStorage and set auth token
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      localStorage.setItem('auth_token', 'mock-jwt-token-' + Math.random());
      
      setUser(updatedUser);
      
      // Track this action
      analytics.trackEvent('user_authenticated', { 
        userId: userData.id, 
        nickname: userData.nickname,
        isAdmin: userData.isAdmin,
        hasCompletedOnboarding: userData.hasCompletedOnboarding
      });
      
      setError(null);
    } catch (error) {
      console.error("Error during login:", error);
      setError("Failed to log in. Please try again.");
      toast.error("Failed to log in. Please try again.");
    }
  };

  // Complete onboarding
  const completeOnboarding = (preferences: {
    nickname?: string, 
    notificationPreference?: 'email' | 'whatsapp' | 'both' | 'none',
    consentToDataTracking: boolean
  }) => {
    if (!user) return;
    
    try {
      const updatedUser = {
        ...user,
        nickname: preferences.nickname || user.nickname,
        notificationPreference: preferences.notificationPreference || user.notificationPreference || 'email',
        hasCompletedOnboarding: true
      };
      
      // Save the updated user
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      // Track this action
      analytics.trackEvent('onboarding_completed', {
        userId: user.id,
        nickname: updatedUser.nickname,
        notificationPreference: updatedUser.notificationPreference,
        consentToDataTracking: preferences.consentToDataTracking
      });
      
      toast.success("Profile setup completed successfully!");
    } catch (error) {
      console.error("Error completing onboarding:", error);
      toast.error("Failed to save your profile settings.");
    }
  };

  // New function to set admin status
  const setAdminStatus = (isAdmin: boolean) => {
    if (!user) {
      toast.error("You need to be logged in to change admin status");
      return;
    }
    
    try {
      const updatedUser = {
        ...user,
        isAdmin
      };
      
      // Save the updated user to localStorage
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      if (isAdmin) {
        toast.success("Admin privileges granted! You can now access the admin dashboard.");
        // Also set admin authentication
        localStorage.setItem('hormony_admin_auth', 'true');
      } else {
        toast.info("Admin privileges revoked.");
        localStorage.removeItem('hormony_admin_auth');
      }
      
    } catch (error) {
      console.error("Error setting admin status:", error);
      toast.error("Failed to update admin status.");
    }
  };

  // Logout function
  const logout = () => {
    try {
      // Track logout before clearing user
      if (user) {
        analytics.trackEvent('user_logout', { 
          userId: user.id, 
          nickname: user.nickname
        });
      }
      
      // Clear user data
      localStorage.removeItem('currentUser');
      localStorage.removeItem('auth_token');
      
      setUser(null);
      setError(null);
      
      toast.info("You have been logged out.");
    } catch (error) {
      console.error("Error during logout:", error);
      toast.error("Failed to log out properly. Please refresh the page.");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        login,
        oauthLogin,
        logout,
        completeOnboarding,
        setAdminStatus
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
